import {useCallback, useRef} from 'react'
import {getDataInquiryGuidingPrompts, getRandomId, getSessionData, setSessionData, getParsedData} from '../helpers'
import {AnalyticsHandler} from '../../../shared/handlers/analytics/handlers'

const useSessionManagement = (
  // State values
  {accounts, prismProgram, messages, isInitialized, sessionId},
  // State setters
  {setSessionId, setIsInitialized, setMessages},
  // External functions
  {post}
) => {
  const initializingRef = useRef(false)
  const initializationPromiseRef = useRef(null)

  const initializeSession = useCallback(async () => {
    // If already initialized, return the current session
    if (isInitialized && sessionId) {
      return sessionId
    }

    // If initialization is in progress, wait for it to complete
    if (initializingRef.current && initializationPromiseRef.current) {
      return initializationPromiseRef.current
    }

    // Start new initialization
    initializingRef.current = true

    const initPromise = (async () => {
      try {
        const cachedSessionId = getSessionData('currentSessionId')
        if (cachedSessionId) {
          const parsedSessionId = getParsedData(cachedSessionId)
          if (parsedSessionId) {
            setSessionId(parsedSessionId)
            setIsInitialized(true)
            return parsedSessionId
          }
        }

        const response = await post(`/apps/ask_healthprism/users/sessions`, {})

        if (response?.ok) {
          const data = await response.json()
          const sessionId = data.id
          setSessionId(sessionId)
          setIsInitialized(true)
          return sessionId
        }
      } catch {
        setIsInitialized(false)
      }

      return null
    })()

    initializationPromiseRef.current = initPromise

    const result = await initPromise

    // Clean up
    initializingRef.current = false
    initializationPromiseRef.current = null

    return result
  }, [setSessionId, setIsInitialized, post, isInitialized, sessionId])

  const populateGuidingPromptsIfReq = useCallback(() => {
    if (messages.length === 0) {
      setMessages([...getDataInquiryGuidingPrompts()])
    }
  }, [messages, setMessages])

  const handleClearHistory = useCallback(async () => {
    setSessionData('messages', [])
    sessionStorage.removeItem('currentSessionId')
    sessionStorage.removeItem('currentUserId')

    setMessages([...getDataInquiryGuidingPrompts()])

    setSessionId(null)
    setIsInitialized(false)

    const newSessionId = await initializeSession()
    if (!newSessionId) {
      setMessages((prevMessages) => [
        ...prevMessages,
        {
          id: getRandomId(),
          type: 'Bot',
          label: `${prismProgram} Agent`,
          responseText: 'Oops, something went wrong on our end. Please try again in a few moments.',
          feedback: -1
        }
      ])
    }

    AnalyticsHandler.onChatbotButtonClick({
      button: {name: 'Clear Chat History'}
    })
  }, [prismProgram, setMessages, setSessionId, setIsInitialized, initializeSession])

  return {
    initializeSession,
    populateGuidingPromptsIfReq,
    handleClearHistory
  }
}

export default useSessionManagement

/*
CHATBOT SESSION MANAGEMENT HOOK

KEY FUNCTIONS:
- initializeSession: Sets up new chat session with user authentication and prompts
- populateGuidingPromptsIfReq: Adds guiding prompts when needed
- handleClearHistory: Clears conversation history and resets session state
- Manages session persistence and data storage

FEATURES:
- User authentication integration
- Session ID generation and management
- Guiding prompts initialization
- Chat history persistence
- Analytics tracking for session events
- Error handling for session operations
*/
